/**
 * Admin Service
 * API calls for admin functionality (users, subscriptions, system)
 * 
 * Subscription API Reference:
 * - GET /admin/subscriptions - List all with filters
 * - GET /admin/subscriptions/{user_id} - Get details
 * - POST /admin/subscriptions/{user_id}/approve - Approve pending
 * - POST /admin/subscriptions/{user_id}/decline - Decline pending
 * - POST /admin/subscriptions/{user_id}/revoke - Revoke active (with refund)
 * - PATCH /admin/subscriptions/{user_id} - Modify subscription
 */
import { authenticatedFetch } from './authService.js';

/**
 * Parse FastAPI error detail into a readable string.
 * Handles arrays of validation errors and plain strings.
 */
function parseApiError(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map(e => {
      const field = e.loc?.filter(l => l !== 'body').join('.') || '';
      const msg = e.msg || '';
      return field ? `${field}: ${msg}` : msg;
    }).filter(Boolean);
    return msgs.length > 0 ? msgs.join('; ') : fallback;
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
}

// Get API base URL at runtime (not at module load time)
const getApiBase = () => {
  let base = window.BATEMO_API?.baseUrl || '/api/v1';
  
  // Force HTTPS if page is served over HTTPS
  if (window.location.protocol === 'https:' && typeof base === 'string' && base.startsWith('http://')) {
    base = base.replace('http://', 'https://');
  }
  
  return base;
};

/**
 * Get all users (admin only)
 * @param {Object} options - Query options
 * @param {number} [options.skip=0] - Number of records to skip
 * @param {number} [options.limit=100] - Max records to return
 * @param {string} [options.search] - Search term for email, first_name, or last_name
 */
export async function getAllUsers({ skip = 0, limit = 50, search = '' } = {}) {
  const API_BASE = getApiBase();
  const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
  if (search) params.append('search', search);
  const url = `${API_BASE}/admin/users?${params}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    const err = new Error(`Failed to fetch users: ${response.statusText}`);
    // Callers (e.g. the Subscriptions page's user lookup) need to tell a
    // permission denial apart from a genuine empty result — attach the
    // status so a 403 doesn't get silently swallowed as "no users found".
    err.status = response.status;
    throw err;
  }

  return response.json();
}

/**
 * Get total user count (admin only)
 * @param {string} [search] - Optional search term to filter count
 */
export async function getUsersCount(search = '') {
  const API_BASE = getApiBase();
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  const url = `${API_BASE}/admin/users/count${params.toString() ? `?${params}` : ''}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch user count: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Set user email verification status (admin only)
 * @param {number} userId - The user ID
 * @param {boolean} verified - Whether email is verified
 */
export async function setEmailVerified(userId, verified) {
  return updateUser(userId, { email_verified: verified });
}

/**
 * Get user by ID (admin only)
 */
export async function getUserById(userId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/users/${userId}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch user: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Delete user by ID (admin only)
 */
export async function deleteUser(userId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/users/${userId}`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    throw new Error(`Failed to delete user: ${response.statusText}`);
  }

  return true;
}

/**
 * Re-send the email-verification (activation) link with a fresh 24h token.
 * 400 if the user is already verified; surface the backend message.
 */
export async function resendActivationEmail(userId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(
    `${API_BASE}/admin/users/${userId}/resend-activation`,
    { method: 'POST' },
  );
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      if (body?.detail) detail = body.detail;
    } catch { /* not JSON, keep statusText */ }
    throw new Error(detail);
  }
  return true;
}

/**
 * Re-send the password-reset link with a fresh 1h token.
 * 400 if the user is inactive; surface the backend message.
 */
export async function resendPasswordResetEmail(userId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(
    `${API_BASE}/admin/users/${userId}/resend-password-reset`,
    { method: 'POST' },
  );
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      if (body?.detail) detail = body.detail;
    } catch { /* not JSON, keep statusText */ }
    throw new Error(detail);
  }
  return true;
}

/**
 * Export all users with subscription details as an Excel report (admin only)
 * Downloads the file directly in the browser.
 */
export async function exportUsersReport() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/users/export`);

  if (!response.ok) {
    throw new Error(`Failed to export users report: ${response.statusText}`);
  }

  // Extract filename from Content-Disposition header or use default
  const disposition = response.headers.get('Content-Disposition');
  let filename = 'batemo_insights_users_report.xlsx';
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match) filename = match[1];
  }

  // Convert response to blob and trigger download
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Get all subscriptions with optional filtering (admin only)
 * @param {Object} filters - Filter options
 * @param {string} [filters.status] - ACTIVE, INACTIVE, CANCELLED, PAST_DUE, TRIALING
 * @param {string} [filters.approval_status] - NONE, PENDING, APPROVED, DECLINED
 * @param {string} [filters.tier] - FREE, PRO, DEMO
 * @param {string} [filters.search] - Search by email or name
 * @param {number} [filters.limit] - Max results (default 50, max 200)
 * @param {number} [filters.offset] - Pagination offset
 */
export async function getSubscriptions(filters = {}) {
  const API_BASE = getApiBase();
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.approval_status) params.append('approval_status', filters.approval_status);
  if (filters.tier) params.append('tier', filters.tier);
  if (filters.search) params.append('search', filters.search);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  
  const queryString = params.toString();
  const url = `${API_BASE}/admin/subscriptions${queryString ? `?${queryString}` : ''}`;
  const response = await authenticatedFetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch subscriptions: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Get all pending subscription requests (admin only)
 * @deprecated Use getSubscriptions({ approval_status: 'PENDING' }) instead
 */
export async function getPendingSubscriptions() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/subscriptions?approval_status=PENDING`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch pending subscriptions: ${response.statusText}`);
  }
  
  const data = await response.json();
  // Return items array for backward compatibility
  return data.items || data;
}

/**
 * Get subscription details for a specific user (admin only)
 * @param {number} userId - The user ID
 */
export async function getSubscriptionDetails(userId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/subscriptions/${userId}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch subscription details: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Approve subscription request (admin only)
 */
export async function approveSubscription(userId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/subscriptions/${userId}/approve`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to approve subscription: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Directly grant a PRO or DEMO subscription to a user (admin only).
 * Bypasses the approval/payment flow. Works even for users who have no
 * subscription row yet — the backend creates the FREE base row on the fly
 * (e.g. seeded knowledge-expert accounts that never went through signup).
 * @param {number} userId - The user ID
 * @param {'PRO'|'DEMO'} tier - Tier to grant
 * @param {number|null} [durationDays] - Optional custom duration in days
 *   (default: 365 for PRO, server demo_duration_days for DEMO)
 */
export async function grantSubscription(userId, tier, durationDays = null) {
  const API_BASE = getApiBase();
  const params = new URLSearchParams({ tier });
  if (durationDays != null) params.set('duration_days', String(durationDays));
  const response = await authenticatedFetch(
    `${API_BASE}/admin/subscriptions/${userId}/grant?${params.toString()}`,
    { method: 'POST' }
  );

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      if (body?.detail) detail = body.detail;
    } catch { /* not JSON, keep statusText */ }
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Decline subscription request (admin only)
 */
export async function declineSubscription(userId, reason) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/subscriptions/${userId}/decline`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || 'No reason provided' })
  });
  
  if (!response.ok) {
    throw new Error(`Failed to decline subscription: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Revoke an active subscription with optional refund (admin only)
 * @param {number} userId - The user ID
 * @param {Object} options - Revoke options
 * @param {string} options.reason - Reason for revocation (required)
 * @param {string} [options.refund_type] - 'full', 'prorated', or 'none' (default: 'full')
 * @param {boolean} [options.notify_user] - Send email notification (default: true)
 */
export async function revokeSubscription(userId, options) {
  const API_BASE = getApiBase();
  const body = {
    reason: options.reason,
    refund_type: options.refund_type || 'full',
    notify_user: options.notify_user !== false
  };
  if (options.violation_type) {
    body.violation_type = options.violation_type;
  }
  const response = await authenticatedFetch(`${API_BASE}/admin/subscriptions/${userId}/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to revoke subscription: ${response.statusText}`));
  }

  return response.json();
}

/**
 * Admin: Reactivate a subscription that is set to cancel at period end.
 * @param {number} userId - The user ID
 * @param {boolean} [notifyUser=true] - Send confirmation email to user
 */
export async function adminReactivateSubscription(userId, notifyUser = true) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(
    `${API_BASE}/admin/subscriptions/${userId}/reactivate?notify_user=${notifyUser}`,
    { method: 'POST' }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to reactivate subscription: ${response.statusText}`));
  }

  return response.json();
}

/**
 * Modify subscription details (admin only)
 * @param {number} userId - The user ID
 * @param {Object} changes - Fields to update
 * @param {string} [changes.tier] - FREE, PRO, DEMO
 * @param {string} [changes.status] - ACTIVE, INACTIVE, CANCELLED, PAST_DUE, TRIALING
 * @param {string} [changes.approval_status] - NONE, PENDING, APPROVED, DECLINED
 * @param {string} [changes.current_period_end] - ISO date string
 * @param {string} [changes.trial_end] - ISO date string
 * @param {boolean} [changes.notify_user] - Send email about changes
 */
export async function modifySubscription(userId, changes) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/subscriptions/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to modify subscription: ${response.statusText}`));
  }
  
  return response.json();
}

/**
 * Review a B2B quote request - approve with custom pricing or decline (admin only)
 * 
 * @param {number} userId - The user ID
 * @param {Object} reviewData - Review options
 * @param {string} reviewData.action - 'approve' or 'decline'
 * @param {number} [reviewData.discount_percent] - Discount percentage (0-100) for approve
 * @param {number} [reviewData.custom_amount_cents] - Custom amount in cents (overrides discount)
 * @param {string} [reviewData.decline_reason] - Reason for decline (required if declining)
 * @param {string} [reviewData.admin_notes] - Internal notes (not shown to customer)
 * @param {boolean} [reviewData.notify_user] - Send email to customer (default: true)
 * 
 * @returns {Promise<{
 *   status: string,
 *   action: string,
 *   user_id: number,
 *   user_email: string,
 *   company_name?: string,
 *   quote_id?: string,
 *   quote_url?: string,
 *   amount_total?: number,
 *   discount_applied?: number,
 *   decline_reason?: string,
 *   message: string
 * }>}
 */
export async function reviewQuote(userId, reviewData) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/subscriptions/${userId}/review-quote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reviewData)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to review quote: ${response.statusText}`));
  }
  
  return response.json();
}

/**
 * Confirm bank transfer payment for a B2B invoice (admin only)
 * 
 * Used when a customer has accepted a quote and been sent an invoice for bank transfer,
 * and the admin has confirmed the payment has been received.
 * 
 * @param {number} userId - The user ID
 * @returns {Promise<{
 *   status: string,
 *   message: string,
 *   user_id: number,
 *   invoice_id: string,
 *   invoice_paid_at: string,
 *   amount: number,
 *   currency: string
 * }>}
 */
export async function confirmPayment(userId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/subscriptions/${userId}/confirm-payment`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to confirm payment: ${response.statusText}`));
  }
  
  return response.json();
}

/**
 * Send payment reminder to B2B customer (admin only)
 * 
 * @param {number} userId - The user ID
 * @param {string} [message] - Optional custom message to include
 * @returns {Promise<{
 *   status: string,
 *   user_id: number,
 *   user_email: string,
 *   company_name: string,
 *   amount: number,
 *   reminder_count: number,
 *   reminder_type: string
 * }>}
 */
export async function sendPaymentReminder(userId, message = null) {
  const API_BASE = getApiBase();
  const url = new URL(`${API_BASE}/admin/subscriptions/${userId}/send-reminder`);
  if (message) {
    url.searchParams.append('message', message);
  }
  
  const response = await authenticatedFetch(url.toString(), {
    method: 'POST'
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to send payment reminder: ${response.statusText}`));
  }
  
  return response.json();
}

/**
 * Get system health status (admin only)
 * Uses obfuscated endpoint path from backend config
 */
export async function getSystemHealth() {
  const API_BASE = getApiBase();
  
  try {
    // First, fetch the config to get the health endpoint path (requires auth)
    const configResponse = await authenticatedFetch(`${API_BASE}/config`);
    if (!configResponse.ok) {
      throw new Error(`Failed to fetch API config: ${configResponse.statusText}`);
    }
    
    const config = await configResponse.json();
    const healthPath = config.health_endpoint_path;
    
    if (!healthPath) {
      throw new Error('Health endpoint path not provided by server');
    }
    
    // Now fetch the actual health data using the obfuscated path (also requires auth)
    const healthResponse = await authenticatedFetch(`${API_BASE}/${healthPath}_admin`);
    
    if (!healthResponse.ok) {
      throw new Error(`Failed to fetch system health: ${healthResponse.statusText}`);
    }
    
    return healthResponse.json();
  } catch (error) {
    throw new Error(`Failed to fetch system health: ${error.message}`);
  }
}

/**
 * Update user (admin only)
 * Can update: email, title, first_name, last_name, company, is_active, admin_role, email_verified, roles
 * @param {number} userId - The user ID
 * @param {Object} updates - Fields to update
 */
export async function updateUser(userId, updates) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/users/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to update user: ${response.statusText}`));
  }
  
  return response.json();
}

/**
 * Create a new demo user directly (admin only).
 * Skips registration & verification, sends welcome email with password-set link.
 * @param {Object} data - User data
 * @param {string} data.email - Email address
 * @param {string} data.first_name - First name
 * @param {string} data.last_name - Last name
 * @param {string} [data.company] - Company (optional)
 * @param {boolean} [data.schedule_nurture=true] - Opt user in to marketing consent and schedule Day 3/5/7/14 nurture emails
 */
export async function createDemoUser(data) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/users/create-demo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to create demo user: ${response.statusText}`));
  }

  return response.json();
}

/**
 * Activate user (admin only)
 */
export async function activateUser(userId) {
  return updateUser(userId, { is_active: true });
}

/**
 * Deactivate user (admin only)
 */
export async function deactivateUser(userId) {
  return updateUser(userId, { is_active: false });
}

/**
 * Clear all API caches (admin only)
 */
export async function clearAllCaches() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/products/cache/clear`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to clear caches: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Clear product cache (admin only)
 */
export async function clearProductCache(productId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/products/cache/clear/${productId}`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to clear product cache: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Clear API cache (admin only) - deprecated, use clearAllCaches
 */
export async function clearCache() {
  return clearAllCaches();
}

// ===== NEW CACHE MANAGEMENT API =====

/**
 * Get cache statistics (admin only)
 * @returns {Promise<Object>} Cache stats including memory usage, key counts, etc.
 */
export async function getCacheStats() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/cache/stats`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch cache stats: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * List cache keys (admin only)
 * @param {string} pattern - Redis key pattern (default: "*")
 * @param {number} limit - Max keys to return (default: 100)
 * @returns {Promise<Array>} List of cache keys with TTL and size
 */
export async function getCacheKeys(pattern = '*', limit = 100) {
  const API_BASE = getApiBase();
  const params = new URLSearchParams({ pattern, limit: limit.toString() });
  const response = await authenticatedFetch(`${API_BASE}/admin/cache/keys?${params}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch cache keys: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Clear metadata cache (admin only)
 * Clears product filters, ranges, and statistics cache.
 */
export async function clearMetadataCache() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/cache/clear/metadata`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to clear metadata cache: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Clear products cache (admin only)
 * Clears product list and individual product data cache.
 */
export async function clearProductsCache() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/cache/clear/products`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to clear products cache: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Clear pictures cache (admin only)
 * Clears all product picture caches.
 */
export async function clearPicturesCache() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/cache/clear/pictures`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to clear pictures cache: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Clear monitoring/radar cache (admin only)
 */
export async function clearMonitoringCache() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/cache/clear/monitoring`, {
    method: 'POST'
  });

  if (!response.ok) {
    throw new Error(`Failed to clear monitoring cache: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Clear ALL caches (admin only)
 * ⚠️ Use with caution - may impact performance temporarily.
 */
export async function clearAllCachesNew() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/cache/clear/all`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to clear all caches: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Delete a specific cache key (admin only)
 * @param {string} key - Exact cache key to delete
 */
export async function deleteCacheKey(key) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/cache/key/${encodeURIComponent(key)}`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    throw new Error(`Failed to delete cache key: ${response.statusText}`);
  }

  return response.json();
}


// ===== PROMO CODE MANAGEMENT API =====

/**
 * Get all promo codes (admin only)
 * @param {Object} filters - Filter options
 * @param {number} [filters.limit=50] - Max results
 * @param {number} [filters.offset=0] - Pagination offset
 * @param {string} [filters.search] - Search by code or description
 * @param {boolean} [filters.activeOnly=false] - Only show active codes
 */
export async function getPromoCodes(filters = {}) {
  const API_BASE = getApiBase();
  const params = new URLSearchParams();

  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  if (filters.search) params.append('search', filters.search);
  if (filters.activeOnly) params.append('active_only', 'true');

  const queryString = params.toString();
  const url = `${API_BASE}/admin/promo-codes${queryString ? `?${queryString}` : ''}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch promo codes: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new promo code (admin only)
 * @param {Object} promoData - Promo code data
 * @param {string} promoData.code - The promo code (e.g., "LAUNCH20")
 * @param {number} promoData.discount_percent - Discount percentage (0-100)
 * @param {string} [promoData.description] - Internal description
 * @param {string} [promoData.expires_at] - ISO date string for expiration
 * @param {boolean} [promoData.is_active=true] - Whether code is active
 * @param {boolean} [promoData.new_customers_only=false] - Only for new customers
 */
export async function createPromoCode(promoData) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(promoData)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to create promo code: ${response.statusText}`));
  }

  return response.json();
}

/**
 * Get a single promo code (admin only)
 * @param {number} promoCodeId - The promo code ID
 */
export async function getPromoCode(promoCodeId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes/${promoCodeId}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch promo code: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update a promo code (admin only)
 * @param {number} promoCodeId - The promo code ID
 * @param {Object} updates - Fields to update
 */
export async function updatePromoCode(promoCodeId, updates) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes/${promoCodeId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to update promo code: ${response.statusText}`));
  }

  return response.json();
}

/**
 * Delete (deactivate) a promo code (admin only)
 * @param {number} promoCodeId - The promo code ID
 */
export async function deletePromoCode(promoCodeId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes/${promoCodeId}`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    throw new Error(`Failed to delete promo code: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get redemptions for a promo code (admin only)
 * @param {number} promoCodeId - The promo code ID
 * @param {Object} filters - Filter options
 * @param {number} [filters.limit=50] - Max results
 * @param {number} [filters.offset=0] - Pagination offset
 */
export async function getPromoCodeRedemptions(promoCodeId, filters = {}) {
  const API_BASE = getApiBase();
  const params = new URLSearchParams();

  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);

  const queryString = params.toString();
  const url = `${API_BASE}/admin/promo-codes/${promoCodeId}/redemptions${queryString ? `?${queryString}` : ''}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch redemptions: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Reset a promo code redemption so the user can use the code again (admin only)
 * @param {number} promoCodeId - The promo code ID
 * @param {number} redemptionId - The redemption ID to reset
 */
export async function resetPromoCodeRedemption(promoCodeId, redemptionId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(
    `${API_BASE}/admin/promo-codes/${promoCodeId}/redemptions/${redemptionId}/reset`,
    { method: 'POST' }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseApiError(errorData.detail, `Failed to reset redemption: ${response.statusText}`));
  }

  return response.json();
}

// ===== AUDIT LOG API =====

/**
 * Get admin audit logs with filters and pagination (admin only)
 * @param {Object} params - Query parameters
 * @param {number} [params.admin_user_id] - Filter by admin user
 * @param {string} [params.action] - Filter by action type
 * @param {string} [params.resource_type] - Filter by resource type
 * @param {string} [params.search] - Search in details, action, resource_id
 * @param {string} [params.start_date] - ISO date string
 * @param {string} [params.end_date] - ISO date string
 * @param {number} [params.limit=50] - Max results
 * @param {number} [params.offset=0] - Pagination offset
 */
export async function getAuditLogs(params = {}) {
  const API_BASE = getApiBase();
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') query.set(key, val);
  });
  const url = `${API_BASE}/admin/audit-logs?${query}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch audit logs: ${response.statusText}`);
  }

  return response.json();
}

export async function getConversionFeedback(params = {}) {
  const API_BASE = getApiBase();
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') query.set(key, val);
  });
  const url = `${API_BASE}/admin/conversion-feedback?${query}`;
  const response = await authenticatedFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch conversion feedback: ${response.statusText}`);
  }
  return response.json();
}

export function getConversionFeedbackCsvUrl(params = {}) {
  const API_BASE = getApiBase();
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') query.set(key, val);
  });
  return `${API_BASE}/admin/conversion-feedback/export.csv?${query}`;
}

/**
 * List Ion admin feedback (admin only). `params` may include
 * status, category, area, severity, scope, assigned_to ('mine' | id),
 * date_from, date_to, limit, offset.
 */
export async function getIonFeedback(params = {}) {
  const API_BASE = getApiBase();
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') query.set(key, val);
  });
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-feedback?${query}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch Ion feedback: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Resolve/reopen and/or reassign a feedback item (admin only).
 * @param {number} id
 * @param {{status?: 'open'|'resolved', assignedToUserId?: number|null, assignedToCleared?: boolean}} body
 */
export async function updateIonFeedback(id, body) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-feedback/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Failed to update Ion feedback: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch the "Export for Claude Code" Markdown bundle as text (admin only).
 * Fetched via authenticatedFetch (Bearer) rather than a plain link so the
 * same body powers both copy-to-clipboard and download.
 */
export async function fetchIonFeedbackBundle(id) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-feedback/${id}/export.md`);
  if (!response.ok) {
    throw new Error(`Failed to export feedback bundle: ${response.statusText}`);
  }
  return response.text();
}

/**
 * Fetch the screenshot attached to a feedback item as a Blob (admin only).
 * Returns null if there is none. Fetched via authenticatedFetch so the
 * Bearer token is sent (an <img src> wouldn't carry it).
 */
export async function fetchIonFeedbackScreenshot(id) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-feedback/${id}/screenshot`);
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to fetch screenshot: ${response.statusText}`);
  }
  return response.blob();
}

/**
 * Read the current Ion-feedback routing owners + admin candidates (admin only).
 * → { frontend: {userId,email}|null, backend: {userId,email}|null, candidates: [...] }
 */
export async function getFeedbackOwners() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/chat-management/feedback-owners`);
  if (!response.ok) {
    throw new Error(`Failed to fetch feedback owners: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Set both feedback-routing owners (admin only). Pass null to unassign an area.
 * @param {{frontendUserId: number|null, backendUserId: number|null}} body
 */
export async function setFeedbackOwners(body) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/chat-management/feedback-owners`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Failed to set feedback owners: ${response.statusText}`);
  }
  return response.json();
}

/**
 * AI eval dashboard (feature/ion-eval-dashboard, Phase 1 — curate + visualize
 * only; runs are imported offline, never triggered from here).
 */
export async function getIonEvalCandidates(params = {}) {
  const API_BASE = getApiBase();
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') query.set(key, val);
  });
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/candidates?${query}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch eval candidates: ${response.statusText}`);
  }
  return response.json();
}

export async function getIonEvalRuns(params = {}) {
  const API_BASE = getApiBase();
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') query.set(key, val);
  });
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/runs?${query}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch eval runs: ${response.statusText}`);
  }
  return response.json();
}

export async function getIonEvalRun(id) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/runs/${id}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch eval run: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Download a full run report: config + summary + every per-case trajectory +
 * DeepEval scores. Distinct from getIonEvalRun (which omits trajectories for
 * the 4s poll). Returns the parsed `{schema, run}` bundle; the caller wraps it
 * in a Blob download (see AdminIonEvalSubtab).
 */
export async function exportIonEvalRunBundle(runId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/runs/${runId}/export`);
  if (!response.ok) {
    throw new Error(`Failed to export eval run: ${response.statusText}`);
  }
  return response.json();
}

/**
 * The SAME run report as exportIonEvalRunBundle, rendered server-side as a
 * self-contained Markdown document (config + aggregates + failure index +
 * per-case prompt/expectation/answer/tool-calls/assertions/judge reasons).
 * Meant to be pasted into an AI assistant for "how do I improve Ion?".
 * Returns the raw markdown text — the caller wraps it in a Blob download.
 */
export async function exportIonEvalRunMarkdown(runId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/runs/${runId}/export.md`);
  if (!response.ok) {
    throw new Error(`Failed to export eval run report: ${response.statusText}`);
  }
  return response.text();
}

/** Download every sibling run of a multi-model comparison as one
 *  `{schema, compareGroupId, runs}` bundle. */
export async function exportIonEvalRunGroupBundle(compareGroupId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(
    `${API_BASE}/admin/ion-eval/runs/group/${compareGroupId}/export`
  );
  if (!response.ok) {
    throw new Error(`Failed to export eval run group: ${response.statusText}`);
  }
  return response.json();
}

/** The comparison bundle as one Markdown document (see
 *  exportIonEvalRunMarkdown). Returns raw markdown text. */
export async function exportIonEvalRunGroupMarkdown(compareGroupId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(
    `${API_BASE}/admin/ion-eval/runs/group/${compareGroupId}/export.md`
  );
  if (!response.ok) {
    throw new Error(`Failed to export eval comparison report: ${response.statusText}`);
  }
  return response.text();
}

// ---------------------------------------------------------------------------
// Eval cases — READ-ONLY. The corpus is a repo file
// (`backend/app/api/v1/chat/eval/cases/cases.json`) deployed with the code; it is changed
// in a merge request, not through this API. The former CRUD and its
// author/approver proposal queue were removed with the table.
// ---------------------------------------------------------------------------

export async function listIonEvalCases(params = {}) {
  const API_BASE = getApiBase();
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') query.set(key, val);
  });
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/cases?${query}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch eval cases: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Trigger the Phase 6 run configurator. `payload` shape:
 * {caseNames?, suite?, models: string[], repeats?, toolsEnabled?,
 *  webSearchEnabled?, knowledgeEnabled?, temperature?, graders?,
 *  judgeModel?, promptVersion?, baselineRunId?, maxCostUsd?, confirm?,
 *  engines?: ('classic'|'native:ion'|'native:baseline')[]}
 *
 * `engines` is omitted for a classic-only run (the server default), so that
 * request is byte-for-byte what it was before engines existed. The server makes
 * one run per (engine, model) the engine can run; native engines run only the
 * native model ids (`native/config.py` NATIVE_MODELS) and refuse
 * toolsEnabled=false.
 *
 * WHOSE BUDGET: eval spend is billed to the synthetic **Ion eval account**,
 * never to the admin who triggers the run — so every `headroom` figure here
 * describes that account's windows, not the caller's. (An earlier revision of
 * this docstring and of the eval drawer claimed the caller's own Ion budget was
 * charged; it never was.)
 *
 * Pre-flight outcomes, in the order the server evaluates them (a 422 without
 * an estimate body, i.e. request validation or `ion_next_disabled`, is thrown
 * as a plain Error with the server's message):
 *
 *  - **429 + `kind: 'cost_ceiling'`** — the estimate exceeds the absolute
 *    per-run cost ceiling. Hard block, `confirm` is irrelevant.
 *  - **429 + `kind: 'ion_next_daily_cap'`** — a native engine's estimate does
 *    not fit in what is left of the Ion Next daily cap. Not overridable.
 *  - **429 + `kind: 'eval_budget'`** — the estimate exceeds the eval account's
 *    remaining budget. Also NOT overridable by `confirm: true`; the message
 *    names the remaining amount and the window's reset date, so surface it
 *    verbatim.
 *  - **422 confirm step** — the estimate exceeds the free threshold and/or
 *    would overrun a finite daily/weekly eval window. Body:
 *    {estimatedCostUsd, nCases, models, repeats, headroom, exceedsHeadroom,
 *    message}. Returned as `{ ...body, _needsConfirm: true }` rather than
 *    thrown, so the UI can show the estimate and a confirm button; the caller
 *    re-submits with confirm:true.
 *  - **202 accepted** — {runIds, compareGroupId, estimatedCostUsd, headroom,
 *    exceedsHeadroom}. `headroom` here is a PRE-run snapshot (the launched
 *    run's own spend is not deducted yet), and `exceedsHeadroom` can be true
 *    (a deliberately confirmed run).
 *
 * Both 429s are thrown as an Error carrying `status: 429` plus that `kind`
 * discriminator. The server sends a plain `detail` STRING with no machine
 * code, so `kind` is derived from the message text — keep the marker phrase
 * below in sync with `ion_eval.py`'s refusal message if that copy changes; a
 * mis-classified refusal only degrades the banner heading (the server's own
 * message is always shown), never the block itself.
 *
 * `headroom` is always an object on the 422 and 202 bodies: {dailyUsd,
 * weeklyUsd, monthlyUsd}, each a plain USD decimal string floored at "0.00" or
 * `null` when that window has no ceiling at all.
 *
 * `dailyUsd` is normally a REAL NUMBER even though the eval account's daily
 * override is the unlimited sentinel: the server reports the effective ceiling
 * `ensure_within_quota` substitutes in that case — the global runaway brake
 * (`tier_limits_config.daily_usd_unlimited_cap`) — because that is the limit
 * which actually aborts a big run mid-flight. It is `null` ONLY when the brake
 * is switched off (cap 0). `weeklyUsd` is `null` in the shipped config for the
 * opposite reason: there is no weekly counterpart to the brake, so an unlimited
 * weekly window genuinely has no ceiling. Do not render a `null` daily as
 * "unlimited" without saying the brake is off (see `fmtHeadroomLine` in
 * AdminIonEvalSubtab.jsx).
 */
function evalRunRequestErrorMessage(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => String(d?.msg || '').replace(/^Value error, /, '')).filter(Boolean).join('; ');
  }
  return detail?.message || null;
}

export async function triggerIonEvalRun(payload) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => null);
  // Only the estimate body is a confirm step. The same status also carries
  // request validation (an engine without a model it can run, toolsEnabled=false
  // on a native engine: a FastAPI `detail` list) and `ion_next_disabled` (a
  // `{code, message}` detail); confirming cannot fix either, so they are errors.
  if (response.status === 422 && typeof body?.estimatedCostUsd === 'number') {
    return { ...body, _needsConfirm: true };
  }
  if (response.status === 422) {
    throw new Error(evalRunRequestErrorMessage(body?.detail) || `Eval run rejected: ${response.statusText}`);
  }
  if (response.status === 429) {
    const detail =
      typeof body?.detail === 'string' ? body.detail : `Eval run refused: ${response.statusText}`;
    const error = new Error(detail);
    error.status = 429;
    error.kind = /remaining eval budget/i.test(detail)
      ? 'eval_budget'
      : /Ion Next daily cap/i.test(detail)
        ? 'ion_next_daily_cap'
        : 'cost_ceiling';
    throw error;
  }
  if (!response.ok) {
    throw new Error(body?.detail || `Failed to trigger eval run: ${response.statusText}`);
  }
  return body;
}

/**
 * The Ion eval account's budget — what the eval suite may spend, what it has
 * spent, and what is left this month and today.
 *
 * Whose budget: the SYNTHETIC eval account's (`chat/eval/eval_user.py`), the
 * billing identity of every eval run — never the calling admin's. The route
 * resolves that account server-side, so no caller needs (or could mistype) its
 * user id.
 *
 * Computed by the SAME backend helper the run configurator gates on
 * (`admin/ion_eval.py::_eval_budget_headroom`), so the budget card and the
 * confirm dialog cannot report different remaining amounts.
 *
 * Shape (all money as fixed-2 decimal STRINGS, never floats):
 * {evalUserEmail, monthlyLimitUsd, activeTopupUsd, effectiveMonthlyUsd,
 *  monthlyUsedUsd, monthlyRemainingUsd, monthlyUsedPct, monthlyResetsAt,
 *  dailyUsedUsd, dailyLimitUsd, dailyRemainingUsd, dailyIsBrake}.
 *
 * Two fields are easy to misread and must be rendered deliberately:
 *  - `monthlyLimitUsd` is the BASE ceiling and `activeTopupUsd` the sum of
 *    top-ups active in the current cycle; `effectiveMonthlyUsd` is their sum,
 *    i.e. what enforcement compares against. Keep them visually separate —
 *    the top-up evaporates at `monthlyResetsAt`, the base limit does not.
 *  - `dailyIsBrake: true` means `dailyLimitUsd` is the global runaway brake
 *    (`tier_limits_config.daily_usd_unlimited_cap`), not a tier/override value.
 *    It is still a hard ceiling that stops runs, so never print "unlimited"
 *    for it. A `null` daily limit means the brake itself is disabled (cap 0).
 *
 * A `null` limit/remaining/`monthlyResetsAt` with zeroed usage is the degraded
 * case (the eval account's subscription cannot be resolved) — the route logs
 * and returns nulls rather than 500ing.
 */
export async function getIonEvalBudget() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/budget`);
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || `Failed to fetch eval budget: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Raise the Ion eval account's monthly ceiling for the CURRENT CYCLE ONLY.
 *
 * Not a base-limit change: this writes one `ion_limit_topups` row with
 * `applies_until = NULL` through the very same helper as the per-user admin
 * grant (`admin/chat_management.py::grant_monthly_topup`), which is what "this
 * cycle only" means everywhere else in Ion — it applies to the next eval turn
 * and expires by itself at the cycle boundary, with no cleanup. The permanent
 * knob is `ION_EVAL_USER_MONTHLY_LIMIT_USD` (a settings change, i.e. a deploy)
 * or the eval account's per-user monthly override in AI Management.
 *
 * Returns `{id, amountUsd, appliesFrom, appliesUntil, budget}` where `budget`
 * is the recomputed `getIonEvalBudget()` shape ALREADY including this grant —
 * repaint the card from it instead of issuing a second GET.
 *
 * @param {{amountUsd: number|string, note?: string|null}} body — `amountUsd`
 *   must be > 0 (the server answers 400 "amountUsd must be > 0" otherwise).
 */
export async function grantIonEvalBudgetTopup({ amountUsd, note = null }) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/budget/topup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amountUsd, note }),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(
      typeof detail?.detail === 'string'
        ? detail.detail
        : `Failed to grant eval budget top-up: ${response.statusText}`
    );
  }
  return response.json();
}

/**
 * Per-case drill-down (fetched on demand when a case row is expanded — NOT
 * embedded in getIonEvalRun, which is polled every 4s while a run is in
 * flight). Returns {trajectory, deepevalMetrics, caseDefinition,
 * caseDefinitionFound, ...}. `caseDefinitionFound: false` means the deployed
 * corpus carries no case by that name — usually because it was renamed or
 * removed in a merge request after the run. The prompt shown (if any) may not
 * reflect exactly what ran.
 */
export async function getIonEvalCaseResult(runId, caseResultId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(
    `${API_BASE}/admin/ion-eval/runs/${runId}/cases/${caseResultId}`
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch eval case result: ${response.statusText}`);
  }
  return response.json();
}

export async function getIonEvalRunGroup(compareGroupId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/ion-eval/runs/group/${compareGroupId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch eval run group: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get distinct action types and resource types for audit log filter dropdowns (admin only)
 */
export async function getAuditLogActions() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/audit-logs/actions`);

  if (!response.ok) {
    throw new Error(`Failed to fetch audit log actions: ${response.statusText}`);
  }

  return response.json();
}

// ===== CELL ACCESS ANALYTICS API =====

/**
 * Get cell access analytics with suspicion scoring (admin only)
 * @param {Object} params - Query parameters
 * @param {number} [params.period_days=30] - Analysis period in days
 * @param {number} [params.user_id] - Filter by specific user
 * @param {number} [params.min_requests=1] - Minimum full data requests
 * @param {number} [params.limit=50] - Max results
 * @param {number} [params.offset=0] - Pagination offset
 */
export async function getCellAccessAnalytics(params = {}) {
  const API_BASE = getApiBase();
  const query = new URLSearchParams();
  if (params.period_days) query.set('period_days', params.period_days);
  if (params.user_id) query.set('user_id', params.user_id);
  if (params.min_requests) query.set('min_requests', params.min_requests);
  if (params.limit) query.set('limit', params.limit);
  if (params.offset) query.set('offset', params.offset);

  const url = `${API_BASE}/admin/analytics/cell-access?${query}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch cell access analytics: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get cell access overview with trends, heatmap, top cells, severity breakdown (admin only)
 * @param {number} [periodDays=30] - Analysis period in days
 */
export async function getCellAccessOverview(periodDays = 30) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}/admin/analytics/cell-access/overview?period_days=${periodDays}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch cell access overview: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Compare cell access patterns for multiple users (admin only)
 * @param {number[]} userIds - Array of user IDs to compare (max 5)
 * @param {number} [periodDays=30] - Analysis period in days
 */
export async function compareCellAccess(userIds, periodDays = 30) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}/admin/analytics/cell-access/compare?user_ids=${userIds.join(',')}&period_days=${periodDays}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to compare cell access: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get per-user cell access timeline (admin only)
 * @param {number} userId - The user ID
 * @param {number} [periodDays=30] - Analysis period in days
 */
export async function getCellAccessTimeline(userId, periodDays = 30) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}/admin/analytics/cell-access/${userId}/timeline?period_days=${periodDays}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch cell access timeline: ${response.statusText}`);
  }

  return response.json();
}

// ===== ANONYMOUS TRAFFIC ANALYTICS API =====
//
// Ported Grafana forensics panels. These are the ANONYMOUS counterpart to the
// cell-access endpoints above: they aggregate api_metrics rows that have no
// user_id, which the per-user cell-access surface structurally cannot show.
// IPs come back pre-hashed (truncated ip_hash), never a raw address.
//
// NOTE: /overview's `potential_scrapers` + `bot_requests_24h` and the whole
// /suspicion payload are computed on a HARDCODED 24h window server-side —
// period_days does not apply to them. The view labels those panels "(24h)".

/**
 * Get the anonymous-traffic overview bundle (admin only).
 * Panels 2, 8, 10, 14, 16, 17.
 * @param {number} [periodDays=30] - Analysis period in days
 * @returns {Promise<{requests_per_ip: Array, cell_views_per_ip: Array,
 *   potential_scrapers: Array, bot_requests_24h: number,
 *   top_bot_user_agents: Array, no_referer_ips: Array,
 *   period_days: number, start_date: string, end_date: string}>}
 */
export async function getAnonymousTrafficOverview(periodDays = 30) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}/admin/analytics/anonymous-traffic/overview?period_days=${periodDays}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch anonymous traffic overview: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get the most-viewed cells by anonymous visitors (admin only). Panel 9.
 * @param {number} [periodDays=30] - Analysis period in days
 * @returns {Promise<{cells: Array<{product_id: string, bid: string,
 *   name: string, views: number, unique_ips: number}>}>}
 */
export async function getAnonymousTrafficTopCells(periodDays = 30) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}/admin/analytics/anonymous-traffic/top-cells?period_days=${periodDays}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch anonymous traffic top cells: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get the hourly bot-vs-human request timeseries (admin only). Panel 15.
 * @param {number} [periodDays=30] - Analysis period in days
 * @returns {Promise<{buckets: Array<{time: string, bots: number, humans: number}>}>}
 */
export async function getAnonymousTrafficTimeseries(periodDays = 30) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}/admin/analytics/anonymous-traffic/timeseries?period_days=${periodDays}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch anonymous traffic timeseries: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get scored/flagged anonymous IPs (admin only). Panel 18, fixed 24h window.
 * @param {number} [periodDays=30] - Analysis period in days
 * @returns {Promise<{scored_ips: Array<{ip: string, total_requests: number,
 *   unique_cells: number, bot_pct: number, no_referer_pct: number,
 *   no_accept_language_pct: number, suspicion_score: number, flags: string[]}>}>}
 */
export async function getAnonymousTrafficSuspicion(periodDays = 30) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}/admin/analytics/anonymous-traffic/suspicion?period_days=${periodDays}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch anonymous traffic suspicion scores: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get anonymous requests grouped by country (admin only). Panel 13.
 * @param {number} [periodDays=30] - Analysis period in days
 * @returns {Promise<{countries: Array<{country_code: string, country: string,
 *   requests: number}>}>}
 */
export async function getAnonymousTrafficGeo(periodDays = 30) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}/admin/analytics/anonymous-traffic/geo?period_days=${periodDays}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch anonymous traffic geo data: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get history/audit log for a promo code (admin only)
 * @param {number} promoCodeId - The promo code ID
 * @param {Object} [filters] - Filter options
 * @param {number} [filters.limit=50] - Max records
 * @param {number} [filters.offset=0] - Offset for pagination
 */
export async function getPromoCodeHistory(promoCodeId, filters = {}) {
  const API_BASE = getApiBase();
  const params = new URLSearchParams();

  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);

  const queryString = params.toString();
  const url = `${API_BASE}/admin/promo-codes/${promoCodeId}/history${queryString ? `?${queryString}` : ''}`;
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch promo code history: ${response.statusText}`);
  }

  return response.json();
}

// ===== FUNNEL TRENDS ANALYTICS API =====

function _trendParams({ periodDays, bucket, excludeAdmin }) {
  const params = new URLSearchParams();
  if (periodDays != null) params.append('period_days', String(periodDays));
  if (bucket) params.append('bucket', bucket);
  if (excludeAdmin != null) params.append('exclude_admin', String(excludeAdmin));
  return params;
}

async function _getTrend(path, params) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}${path}?${params}`;
  const response = await authenticatedFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
  }
  return response.json();
}

/** LF1: Active demos + LP→demo conversion per bucket. */
export async function getActiveDemoTrend({ periodDays = 90, bucket = 'week', excludeAdmin = true } = {}) {
  return _getTrend('/admin/analytics/funnel/active-demo-trend', _trendParams({ periodDays, bucket, excludeAdmin }));
}

/** LF2: Landing-page visitor source breakdown over time. */
export async function getLandingpageSourcesTrend({ periodDays = 90, bucket = 'week' } = {}) {
  return _getTrend('/admin/analytics/landingpage-sources-trend', _trendParams({ periodDays, bucket }));
}

/** LF2b: Internal source conversion (Explorer→LP, Cell pages→LP). */
export async function getInternalSourceConversion({ periodDays = 90, bucket = 'week' } = {}) {
  return _getTrend('/admin/analytics/internal-source-conversion', _trendParams({ periodDays, bucket }));
}

/** LF3: Average active minutes per demo user. */
export async function getEngagementTrend({ periodDays = 90, bucket = 'week', excludeAdmin = true } = {}) {
  return _getTrend('/admin/analytics/engagement-trend', _trendParams({ periodDays, bucket, excludeAdmin }));
}

/** LF4: Refined funnel sub-stages between Registration and PRO. */
export async function getFunnelSubStages({ periodDays = 30, excludeAdmin = true } = {}) {
  return _getTrend('/admin/analytics/funnel/sub-stages', _trendParams({ periodDays, excludeAdmin }));
}

/** LF5: Trade-fair demos (signup_source=event_signup) + conversion. */
export async function getTradeFairTrend({ periodDays = 180, bucket = 'week' } = {}) {
  return _getTrend('/admin/analytics/trade-fair-trend', _trendParams({ periodDays, bucket }));
}

/** LF6: Active-demo snapshot at each bucket end, split by trade fair vs organic. */
export async function getCumulativeActiveDemos({ periodDays = 365, bucket = 'week' } = {}) {
  return _getTrend('/admin/analytics/cumulative-active-demos', _trendParams({ periodDays, bucket }));
}

// ============================================================================
// DEMO CONFIG + CONVERSION FUNNEL (Slice C+)
// ============================================================================

/**
 * Get the effective demo-limit config (env defaults + DB overrides).
 * @returns {Promise<{daily_limit_demo:number, first_day_limit:number,
 *   demo_duration_days:number, demo_reactivation_days:number,
 *   overrides:Object<string,boolean>}>} `overrides[field]===true` = DB override.
 */
export async function getDemoConfig() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/demo-config`);
  if (!response.ok) {
    let detail;
    try { detail = (await response.json())?.detail; } catch (e) { /* non-JSON */ }
    throw new Error(parseApiError(detail, `Failed to load demo config (${response.status})`));
  }
  return response.json();
}

/**
 * Update demo-limit config (ADMIN only). Send only the changed int fields
 * (server validates 1..1000). Send `null` for a field to CLEAR its override
 * (revert to the env default).
 * @param {Object<string, number|null>} changes
 * @returns {Promise<object>} the new effective config (same shape as GET)
 */
export async function updateDemoConfig(changes) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/demo-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  if (!response.ok) {
    let detail;
    try { detail = (await response.json())?.detail; } catch (e) { /* non-JSON */ }
    throw new Error(parseApiError(detail, `Failed to update demo config (${response.status})`));
  }
  return response.json();
}

/**
 * Get the effective Ion (AI chat) per-tier limits (env defaults + DB overrides).
 *
 * NOTE (2026-07-17): the per-day REQUEST-COUNT cap (`daily_requests`) was
 * removed entirely — the USD daily/weekly/monthly caps are the sole
 * enforcement mechanism now. Do not resurrect that field.
 *
 * @returns {Promise<{
 *   demo:{daily_usd:number, weekly_usd:number, monthly_usd:number},
 *   pro:{daily_usd:number, weekly_usd:number, monthly_usd:number},
 *   defaults:{demo:object, pro:object},
 *   overridden:{demo:Object<string,boolean>, pro:Object<string,boolean>}
 * }>} `overridden[tier][field]===true` = DB override; otherwise env default.
 */
export async function getChatTierLimits() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/chat-management/tier-limits`);
  if (!response.ok) {
    let detail;
    try { detail = (await response.json())?.detail; } catch (e) { /* non-JSON */ }
    throw new Error(parseApiError(detail, `Failed to load Ion tier limits (${response.status})`));
  }
  return _normalizeTierLimits(await response.json());
}

/**
 * Normalize the tier-limits payload to a stable snake_case shape regardless of
 * whether the backend serializes with camelCase aliases (dailyUsd) or the
 * field names (daily_usd). FastAPI serializes response models by alias by
 * default, so this endpoint emits camelCase like the rest of chat-management;
 * consumers read `daily_usd` etc. so we map here in one place.
 */
function _normalizeTierLimits(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const pick = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    return {
      daily_usd: obj.daily_usd ?? obj.dailyUsd,
      weekly_usd: obj.weekly_usd ?? obj.weeklyUsd,
      // "Insights token" ($0.25/token) read-only display equivalents of the
      // USD figures (2026-07-16/-17) — pure derivation, not separate
      // editable values.
      daily_tokens: obj.daily_tokens ?? obj.dailyTokens,
      weekly_tokens: obj.weekly_tokens ?? obj.weeklyTokens ?? '0',
      // Monthly window (Ion AI limits system, 2026-07) — 0/'0' placeholder
      // for the 'unlimited' pseudo-tier (no monthly concept there).
      monthly_usd: obj.monthly_usd ?? obj.monthlyUsd ?? 0,
      monthly_tokens: obj.monthly_tokens ?? obj.monthlyTokens ?? '0',
    };
  };
  return {
    demo: pick(raw.demo),
    pro: pick(raw.pro),
    // 'batemo' pseudo-tier (Ion AI limits system, 2026-07) — same shape as
    // demo/pro.
    batemo: pick(raw.batemo),
    // Single-field pseudo-tier: the unlimited-role runaway/abuse brake
    // (quota.py ensure_within_quota). Reuses the same shape so
    // IonTierLimitsPanel needs no new component (fields=['daily_usd']).
    unlimited: pick(raw.unlimited),
    defaults: {
      demo: pick(raw.defaults?.demo),
      pro: pick(raw.defaults?.pro),
      batemo: pick(raw.defaults?.batemo),
      unlimited: pick(raw.defaults?.unlimited),
    },
    overridden: {
      demo: pick(raw.overridden?.demo),
      pro: pick(raw.overridden?.pro),
      batemo: pick(raw.overridden?.batemo),
      unlimited: pick(raw.overridden?.unlimited),
    },
  };
}

/**
 * Update Ion (AI chat) tier limits (ADMIN only). Send the tier plus only the
 * changed fields. Send `null` for a field to CLEAR its override (revert to the
 * env default). tier='unlimited' (the runaway/abuse brake) only honours
 * `daily_usd`; tier='batemo' honours `daily_usd`/`weekly_usd`/`monthly_usd` —
 * unsupported fields are silently ignored for their pseudo-tier.
 * @param {{tier:'demo'|'pro'|'batemo'|'unlimited',
 *   daily_usd?:number|null, weekly_usd?:number|null, monthly_usd?:number|null}} changes
 * @returns {Promise<object>} the new effective limits (same shape as GET)
 */
export async function updateChatTierLimits(changes) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/chat-management/tier-limits`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    // Snake_case body — the backend model sets `populate_by_name=True`, so it
    // accepts the field names (daily_usd, …) as well as the aliases, and
    // `model_fields_set` (its omitted-vs-null discriminator) is keyed by field
    // name either way.
    body: JSON.stringify(changes),
  });
  if (!response.ok) {
    let detail;
    try { detail = (await response.json())?.detail; } catch (e) { /* non-JSON */ }
    throw new Error(parseApiError(detail, `Failed to update Ion tier limits (${response.status})`));
  }
  return _normalizeTierLimits(await response.json());
}

/**
 * Normalize the agent-config payload to a stable snake_case shape regardless
 * of whether the backend serializes with camelCase aliases or field names —
 * same reasoning as `_normalizeTierLimits` above.
 */
function _normalizeAgentConfigValues(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  return {
    temperature_default: obj.temperature_default ?? obj.temperatureDefault ?? null,
    model_default: obj.model_default ?? obj.modelDefault,
    max_output_tokens: obj.max_output_tokens ?? obj.maxOutputTokens,
    max_iterations: obj.max_iterations ?? obj.maxIterations,
    cache_ttl_default: obj.cache_ttl_default ?? obj.cacheTtlDefault,
    cache_ttl_admin: obj.cache_ttl_admin ?? obj.cacheTtlAdmin,
    websearch_max_per_turn: obj.websearch_max_per_turn ?? obj.websearchMaxPerTurn,
    websearch_max_per_conversation:
      obj.websearch_max_per_conversation ?? obj.websearchMaxPerConversation,
    rate_limit_per_minute: obj.rate_limit_per_minute ?? obj.rateLimitPerMinute,
  };
}

function _normalizeAgentConfig(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  return {
    effective: _normalizeAgentConfigValues(raw.effective),
    defaults: _normalizeAgentConfigValues(raw.defaults),
    overridden: raw.overridden || {},
    allowed_models: raw.allowed_models ?? raw.allowedModels ?? [],
  };
}

/**
 * Env-wide Ion Agent Config (temperature, default model, agent/cache/
 * web-search/rate-limit knobs) — GET /admin/chat-management/agent-config.
 * No per-user dimension (locked decision — see
 * .planning/ai-settings-config-dashboard-plan.md §0.1).
 * @returns {Promise<{effective:object, defaults:object, overridden:Record<string,boolean>, allowed_models:string[]}>}
 */
export async function getAgentConfig() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/chat-management/agent-config`);
  if (!response.ok) {
    let detail;
    try { detail = (await response.json())?.detail; } catch (e) { /* non-JSON */ }
    throw new Error(parseApiError(detail, `Failed to load Ion agent config (${response.status})`));
  }
  return _normalizeAgentConfig(await response.json());
}

/**
 * Update the env-wide Ion Agent Config. Send only the changed field(s); send
 * `null` for a field to CLEAR its override (revert to the env default).
 * `model_default` must be one of `allowed_models` from the GET response (the
 * IAM boundary) — the backend re-validates regardless.
 * @param {object} changes
 * @returns {Promise<object>} the new effective config (same shape as GET)
 */
export async function setAgentConfig(changes) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/chat-management/agent-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  if (!response.ok) {
    let detail;
    try { detail = (await response.json())?.detail; } catch (e) { /* non-JSON */ }
    throw new Error(parseApiError(detail, `Failed to update Ion agent config (${response.status})`));
  }
  return _normalizeAgentConfig(await response.json());
}

// ---------------------------------------------------------------------------
// AI usage analytics (admin/ai-usage/*)
//
// Only the two endpoints with a real parameter surface live here. The rest of
// the AI-usage dashboard still calls authenticatedFetch inline in
// AnalyticsAiUsageView.jsx — those take at most range_days/bucket, so a wrapper
// would add indirection without adding a contract.
// ---------------------------------------------------------------------------

/**
 * One page of per-user Ion AI usage. Sorting and pagination are the SERVER's
 * job: never re-sort the returned page client-side — it is a slice of the
 * population, so the local minimum of a page is not the global minimum, and on
 * a spend table that renders as a confidently wrong "cheapest user".
 *
 * @param {Object} opts
 * @param {number} [opts.rangeDays=30]
 * @param {number} [opts.limit=50] - page size (max 1000)
 * @param {number} [opts.offset=0]
 * @param {string} [opts.sortBy='cost'] - email | role | input_tokens |
 *   output_tokens | cached_input_tokens | total_tokens | cost | requests |
 *   conversations | last_activity
 * @param {'asc'|'desc'} [opts.sortOrder='desc']
 * @returns {Promise<{range_days:number, limit:number, offset:number,
 *   total:number, has_more:boolean, sort_by:string, sort_order:string,
 *   order:string, users:Array<Object>}>}
 */
export async function getAiUsageByUser({
  rangeDays = 30,
  limit = 50,
  offset = 0,
  sortBy = 'cost',
  sortOrder = 'desc',
} = {}) {
  const API_BASE = getApiBase();
  const params = new URLSearchParams({
    range_days: String(rangeDays),
    limit: String(limit),
    offset: String(offset),
    sort_by: sortBy,
    sort_order: sortOrder,
  });
  const response = await authenticatedFetch(`${API_BASE}/admin/ai-usage/by-user?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch AI usage by user: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Per-surface AI spend — which FEATURE spent the money (`ion_llm_calls.surface`),
 * plus the customer-chat role/segment split and a per-bucket timeseries.
 *
 * `includeEval` defaults to true, matching the endpoint: the question is "which
 * feature spent the AWS bill", and an eval run is a feature that spends it. With
 * the default, this total covers the SAME population as /summary (DEC-4 made
 * /summary include eval too), so the two reconcile for the same window. Passing
 * `includeEval: false` is what makes this total sit BELOW /summary whenever an
 * eval ran.
 *
 * @param {Object} opts
 * @param {number} [opts.rangeDays=30]
 * @param {'hour'|'day'} [opts.bucket='day']
 * @param {boolean} [opts.includeEval=true]
 * @returns {Promise<Object>}
 */
export async function getAiUsageSurfaceSpend({
  rangeDays = 30,
  bucket = 'day',
  includeEval = true,
} = {}) {
  const API_BASE = getApiBase();
  const params = new URLSearchParams({
    range_days: String(rangeDays),
    bucket,
    include_eval: String(includeEval),
  });
  const response = await authenticatedFetch(
    `${API_BASE}/admin/ai-usage/surface-spend?${params}`
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch AI surface spend: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Demo→PRO conversion funnel (weekly buckets).
 * @param {number} [days=42]
 * @returns {Promise<{period:{days,start,end,bucket},
 *   weeks:Array<{week_start,limit_hit,sales_inquiry,quote_sent,won}>,
 *   totals:Object}>}
 */
export async function getDemoConversionsFunnel({ days = 42 } = {}) {
  const API_BASE = getApiBase();
  const url = `${API_BASE}/admin/analytics/funnel/demo-conversions?days=${encodeURIComponent(days)}`;
  const response = await authenticatedFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch demo conversions funnel: ${response.statusText}`);
  }
  return response.json();
}


/**
 * List all products (admin) — {product_id, b_num, manufacturer, name}.
 * Backs the certification-maintenance product picker.
 * @returns {Promise<{products:Array, total:number}>}
 */
export async function listAdminProducts() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/products/list`);
  if (!response.ok) {
    throw new Error(`Failed to list products: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Read a product's safety certifications + the allowed code list (admin).
 * @param {string} productId - 16-hex product id
 * @returns {Promise<{product_id:string, certifications:string[], allowed:string[]}>}
 */
export async function getProductCertifications(productId) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(
    `${API_BASE}/admin/products/${productId}/certifications`,
  );
  if (!response.ok) {
    throw new Error(`Failed to load certifications: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Set a product's safety certifications (admin, field-level PATCH — NOT the
 * full product PUT). Codes are validated server-side against the allowlist.
 * @param {string} productId - 16-hex product id
 * @param {string[]} certifications - standardized certification codes
 * @returns {Promise<{product_id:string, certifications:string[]}>}
 */
export async function updateProductCertifications(productId, certifications) {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(
    `${API_BASE}/admin/products/${productId}/certifications`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ certifications }),
    },
  );
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = parseApiError(body?.detail, detail);
    } catch {
      /* keep status text */
    }
    throw new Error(`Failed to update certifications: ${detail}`);
  }
  return response.json();
}

/**
 * Get the current admin's role, resolved permissions, accessible tabs
 * (server-owned tab map — feeds the `adminTabs` signal consumed by
 * canAccessTab), and data-scope info (which environment this request is
 * served from, and whether the analytics-DB redirect is reading
 * production). Called once per admin session to feed the `adminTabs` and
 * `adminEnvironment` signals in state/userState.js.
 * @returns {Promise<{admin_role: string|null, permissions: string[], tabs: string[], environment: string, analytics_reads_production: boolean}>}
 */
export async function getMyPermissions() {
  const API_BASE = getApiBase();
  const response = await authenticatedFetch(`${API_BASE}/admin/me/permissions`);

  if (!response.ok) {
    throw new Error(`Failed to fetch admin permissions: ${response.statusText}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Ion Next (admin-only native engine, backend/app/api/v1/admin/ion_next.py).
// Every route 404s while `ion_next_enabled` is off and needs AI_MANAGE.
// ---------------------------------------------------------------------------

// A stored sandbox file's public id is a random UUID (native/files.py). The
// download URL is rebuilt from it here instead of trusting the
// `download_url` the stream carried.
const ION_NEXT_FILE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

async function _ionNextError(response, what) {
  let detail = response.statusText || `HTTP ${response.status}`;
  try {
    const body = await response.json();
    const d = body?.detail;
    if (d && typeof d === 'object' && !Array.isArray(d)) detail = d.message || d.code || detail;
    else detail = parseApiError(d, detail);
  } catch {
    /* keep status text */
  }
  const err = new Error(`${what}: ${detail}`);
  err.status = response.status;
  return err;
}

/** POST target of the Ion Next stream (same NDJSON as /chat/stream). */
export function ionNextStreamUrl() {
  return `${getApiBase()}/admin/ion-next/stream`;
}

// Conversation scope of the Ion Next admin page: 'mine' (default, the
// caller's own) or 'all' (every admin's, read-only for foreign ones; the
// backend answers 403 when its development-phase switch is off). 'mine'
// sends no parameter, so its URLs are the same as before the scope existed.
function _ionNextScopeQuery(scope) {
  return scope === 'all' ? '?scope=all' : '';
}

/**
 * History URL of one Ion Next conversation; `encodedId` is already URI-encoded.
 * `scope: 'all'` also finds another admin's conversation.
 */
export function ionNextMessagesUrl(encodedId, scope = 'mine') {
  return `${getApiBase()}/admin/ion-next/conversations/${encodedId}/messages${_ionNextScopeQuery(scope)}`;
}

/**
 * Models, active prompt versions + hashes, caps and today's spend.
 * @returns {Promise<{enabled: boolean, defaultModel: string, models: string[],
 *   prompts: Record<string, {version: string, sha256: string, id: number|null}>,
 *   caps: {turnUsd: string, conversationUsd: string, dailyUsd: string},
 *   todaySpendUsd: string}>}
 */
export async function getIonNextStatus() {
  const response = await authenticatedFetch(`${getApiBase()}/admin/ion-next/status`);
  if (!response.ok) throw await _ionNextError(response, 'Failed to load Ion Next status');
  return response.json();
}

// ── Ion Next prompts (ion_native_prompts; plan docs/plans/2026-10-27-ion-next-db-prompt.md) ──

/**
 * Every prompt key with its versions, newest first, without bodies.
 * @returns {Promise<{keys: Array<{key: string, activeId: number|null,
 *   versions: Array<{id: number, key: string, version: number, sha256: string,
 *   note: string|null, isActive: boolean, createdAt: string|null, author: string|null}>}>}>}
 */
export async function listIonNextPrompts() {
  const response = await authenticatedFetch(`${getApiBase()}/admin/ion-next/prompts`);
  if (!response.ok) throw await _ionNextError(response, 'Failed to load the prompts');
  return response.json();
}

/** One prompt version with its `body`. */
export async function getIonNextPrompt(id) {
  const response = await authenticatedFetch(
    `${getApiBase()}/admin/ion-next/prompts/${encodeURIComponent(String(id))}`,
  );
  if (!response.ok) throw await _ionNextError(response, 'Failed to load the prompt');
  return response.json();
}

/**
 * Save a new version of a prompt key; with `activate` (default) new
 * conversations use it at once, running ones keep their pinned version.
 */
export async function createIonNextPrompt({ key, body, note = null, activate = true }) {
  const response = await authenticatedFetch(`${getApiBase()}/admin/ion-next/prompts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, body, note: note || null, activate }),
  });
  if (!response.ok) throw await _ionNextError(response, 'Failed to save the prompt');
  return response.json();
}

/** Make an existing version the active one of its key (a rollback). */
export async function activateIonNextPrompt(id) {
  const response = await authenticatedFetch(
    `${getApiBase()}/admin/ion-next/prompts/${encodeURIComponent(String(id))}/activate`,
    { method: 'POST' },
  );
  if (!response.ok) throw await _ionNextError(response, 'Failed to activate the prompt');
  return response.json();
}

/**
 * Ion Next conversations, newest activity first: the caller's own, or with
 * `scope: 'all'` every admin's (rows then carry `isOwner`, `ownerName`, `ownerEmail`).
 */
export async function listIonNextConversations({ limit = 30, scope = 'mine' } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (scope === 'all') params.set('scope', 'all');
  const response = await authenticatedFetch(`${getApiBase()}/admin/ion-next/conversations?${params}`);
  if (!response.ok) throw await _ionNextError(response, 'Failed to load Ion Next conversations');
  return response.json();
}

/** Soft-delete one of the caller's Ion Next conversations. */
export async function deleteIonNextConversation(publicId) {
  const response = await authenticatedFetch(
    `${getApiBase()}/admin/ion-next/conversations/${encodeURIComponent(publicId)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) throw await _ionNextError(response, 'Failed to delete the conversation');
}

/**
 * Fetch one stored sandbox file as a Blob (never rendered: the caller saves it).
 * @param {string} fileId — the artifact's `file_id` (a UUID)
 * @param {{scope?: 'mine'|'all'}} [opts] — 'all' for a file of another admin's conversation
 * @returns {Promise<Blob>}
 */
export async function downloadIonNextFile(fileId, { scope = 'mine' } = {}) {
  if (!ION_NEXT_FILE_ID_RE.test(String(fileId || ''))) {
    throw new Error('Invalid file id');
  }
  const response = await authenticatedFetch(
    `${getApiBase()}/admin/ion-next/files/${fileId}${_ionNextScopeQuery(scope)}`,
  );
  if (!response.ok) throw await _ionNextError(response, 'Download failed');
  return response.blob();
}
